// A policy body as a person reads it: sections of items, each with labelled rows, in the words of the screen (no ids,
// no JSON keys). The approval screen shows one version this way, or two side by side with what changed marked.

export type Row = { label: string; value: string | string[] };
export type Item = { key: string; title: string; rows: Row[] };
export type ViewSection = { key: string; title: string; items: Item[] };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const IDS = /\s*\((?:[0-9a-f]{8}-[0-9a-f-]{27}(?:,\s*)?)+\)/gi; // "(01a1…, 01a1…)": the evidence ids inside a rationale
const human = (k: string) => k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
const list = (v: unknown) => (Array.isArray(v) ? v.map((x) => String(x)).filter(Boolean) : []);
const text = (v: unknown) => String(v ?? '').replace(IDS, '').trim();
const COUNTRY = (() => { try { return new Intl.DisplayNames(['en'], { type: 'region' }); } catch { return null; } })();
const country = (c: string) => { try { return COUNTRY?.of(c.toUpperCase()) ?? c; } catch { return c; } };
const num = (n: unknown) => Number(n).toLocaleString('en-US');

function icp(b: Record<string, any>): ViewSection[] {
  const hc = (b.hard_constraints ?? {}) as Record<string, any>;
  const range = Array.isArray(hc.employee_range) ? hc.employee_range as number[] : null;
  const who: Row[] = [
    { label: 'Countries', value: list(hc.allowed_countries).map(country).join(', ') || 'Not set' },
    { label: 'Company size', value: range ? (range[1] >= 10_000_000 ? `${num(range[0])}+ employees` : `${num(range[0])}–${num(range[1])} employees`) : 'Not set' },
    { label: 'Business model', value: hc.business_model ? String(hc.business_model) : 'Not set' },
    ...(list(hc.excluded_countries).length ? [{ label: 'Never in', value: list(hc.excluded_countries).map(country).join(', ') }] : []),
    ...(list(hc.excluded_industries).length ? [{ label: 'Excluded industries', value: list(hc.excluded_industries) }] : []),
    ...(hc.exclude_existing_customers ? [{ label: 'Existing customers', value: 'Left out' }] : []),
  ];
  const segs = (Array.isArray(b.hypotheses) ? b.hypotheses : []) as Array<Record<string, any>>;
  return [
    { key: 'who', title: 'Who to target', items: [{ key: 'who', title: 'Every account must match', rows: who }] },
    { key: 'segments', title: `Segments (${segs.length})`, items: segs.map((h, i) => ({
      key: String(h.segment ?? i), title: String(h.segment ?? `Segment ${i + 1}`),
      rows: [
        ...(list(h.buyer_roles).length ? [{ label: 'People to reach', value: list(h.buyer_roles) }] : []),
        ...(h.rationale ? [{ label: 'Why it fits', value: text(h.rationale) }] : []),
        ...(h.validation_test ? [{ label: 'How to test it', value: text(h.validation_test) }] : []),
        ...(h.contradicting_evidence ? [{ label: 'Risks', value: text(h.contradicting_evidence) }] : []),
        { label: 'Source', value: `${h.source === 'ai' ? 'Drafted by AI' : h.source === 'assessment' ? 'From the website assessment' : 'Added by a person'}${list(h.evidence_statement_ids).length ? `, from ${list(h.evidence_statement_ids).length} facts in your business context` : ''}${h.label ? ` · ${String(h.label)}` : ''}` },
      ],
    })) },
    ...((Array.isArray(b.criteria) && b.criteria.length) ? [{ key: 'criteria', title: 'Qualification criteria', items: (b.criteria as Array<Record<string, any>>).map((c, i) => ({ key: String(c.text ?? i), title: String(c.text ?? `Criterion ${i + 1}`), rows: Object.entries(c).filter(([k, v]) => k !== 'text' && k !== 'id' && v !== null && v !== '').map(([k, v]) => ({ label: human(k), value: Array.isArray(v) ? list(v) : String(v) })) })) }] : []),
  ];
}

// a merge field is a key, or { key, description, kind, use, example } (src/outbound/merge.ts)
const mergeField = (f: unknown) => {
  if (typeof f !== 'object' || f === null) return `{{${String(f)}}}`;
  const o = f as Record<string, unknown>;
  const where = o.use === 'first_email' ? ', in the first email' : o.use === 'every_email' ? ', in every email' : '';
  return `{{${String(o.key)}}}${o.kind === 'url' ? ' (link' + where + ')' : where ? ` (${where.slice(2)})` : ''}: ${String(o.description ?? '')}`;
};

function outbound(b: Record<string, any>): ViewSection[] {
  const t = (Array.isArray(b.templates) ? b.templates : []) as Array<{ subject?: string; body?: string }>;
  const caps = (b.caps ?? {}) as Record<string, number>;
  return [
    { key: 'templates', title: `Email templates (${t.length})`, items: t.map((x, i) => ({ key: `t${i}`, title: x.subject ? `Template ${i + 1}: ${x.subject}` : `Template ${i + 1}`, rows: [{ label: 'Subject', value: String(x.subject ?? '') }, { label: 'Body', value: String(x.body ?? '') }] })) },
    { key: 'rules', title: 'Rules for every email', items: [{ key: 'rules', title: 'Footer and limits', rows: [
      { label: 'Footer', value: String(b.footer ?? 'Not set') },
      ...Object.entries(caps).map(([k, v]) => ({ label: k === 'daily_new_leads' ? 'New leads a day' : k === 'campaign_leads' ? 'Leads per campaign' : human(k), value: num(v) })),
      ...(Array.isArray(b.merge_fields) && b.merge_fields.length ? [{ label: 'Your data in emails', value: (b.merge_fields as unknown[]).map(mergeField) }] : []),
      ...(b.language ? [{ label: 'Language', value: b.language === 'en-GB' ? 'British English' : 'American English' }] : []),
    ] }] },
  ];
}

function messages(b: Record<string, any>): ViewSection[] {
  const s = (Array.isArray(b.sequence) ? b.sequence : []) as Array<{ seq_number?: number; delay_in_days?: number; subject?: string; body?: string }>;
  return [{ key: 'emails', title: `The emails (${s.length})`, items: s.map((x, i) => ({ key: `e${x.seq_number ?? i}`, title: `Email ${x.seq_number ?? i + 1} · day ${x.delay_in_days ?? 0}`, rows: [{ label: 'Subject', value: String(x.subject ?? '') }, { label: 'Body', value: String(x.body ?? '') }] })) }];
}

/** Anything else: its fields in words, ids left out, nested values one level down. */
function generic(b: Record<string, any>): ViewSection[] {
  const rows = (o: Record<string, any>): Row[] => Object.entries(o)
    .filter(([k, v]) => !/(^|_)ids?$/.test(k) && !(typeof v === 'string' && UUID.test(v)) && v !== null && !(Array.isArray(v) && !v.length))
    .map(([k, v]) => ({ label: human(k), value: Array.isArray(v) ? v.map((x) => (typeof x === 'object' ? Object.values(x as object).filter((y) => typeof y !== 'object').join(' · ') : String(x))) : typeof v === 'object' ? Object.entries(v).map(([a, c]) => `${human(a)}: ${typeof c === 'object' ? JSON.stringify(c) : String(c)}`) : String(v) }));
  return [{ key: 'all', title: 'What it says', items: [{ key: 'all', title: 'Details', rows: rows(b) }] }];
}

export function policyView(type: string, body: unknown): ViewSection[] {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, any>;
  if (type === 'icp_version') return icp(b);
  if (type === 'outbound_policy') return outbound(b);
  if (type === 'first_campaign_messages') return messages(b);
  return generic(b);
}

/** How an item changed from the older version: new, removed, changed or the same (matched by its key). */
export type Change = 'added' | 'removed' | 'changed' | 'same';
export function itemChanges(older: ViewSection[], newer: ViewSection[]) {
  const sig = (i: Item) => JSON.stringify(i.rows);
  const index = (v: ViewSection[]) => new Map(v.flatMap((s) => s.items.map((i) => [`${s.key}/${i.key}`, i] as const)));
  const a = index(older); const b = index(newer);
  const out = new Map<string, Change>();
  for (const [k, i] of b) out.set(k, !a.has(k) ? 'added' : sig(a.get(k)!) === sig(i) ? 'same' : 'changed');
  for (const k of a.keys()) if (!b.has(k)) out.set(k, 'removed');
  return out;
}
